import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";

/**
 * ContextMenuContext — global settings & shared state for the enhanced
 * right-click system in Bluebird Task Management.
 *
 * Currently exposes:
 *   - enhancedRightClickEnabled: global on/off toggle (synced from backend)
 *
 * Future-proof: add any cross-cutting context-menu state here
 * (e.g. user-defined quick links, pinned actions, etc.)
 */

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const ContextMenuContext = createContext();

export const useContextMenuSettings = () => {
    const context = useContext(ContextMenuContext);
    if (!context) {
        throw new Error(
            "useContextMenuSettings must be used within a ContextMenuProvider"
        );
    }
    return context;
};

export const ContextMenuProvider = ({ children }) => {
    // Default to true; overwritten by backend preference once user is authenticated
    const [enhancedRightClickEnabled, setEnhancedRightClickEnabled] = useState(true);
    const { user, token } = useAuth();

    // Sync the right-click preference from the shared backend whenever the
    // authenticated user changes. This keeps the setting consistent across
    // bluebird-documentation and bluebird-task-management.
    useEffect(() => {
        if (!user || !token) return;

        const fetchRightClickPreference = async () => {
            try {
                const response = await fetch(`${API_BASE}/users/check-right-click-status/`, {
                    headers: { 'Authorization': token, "auth-token": `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setEnhancedRightClickEnabled(data.enhanced_right_click_enabled);
                }
            } catch (err) {
                console.error('Error fetching right-click preference:', err);
            }
        };

        fetchRightClickPreference();
    }, [user?.uid, token]);

    const toggleEnhancedRightClick = useCallback(() => {
        setEnhancedRightClickEnabled((prev) => !prev);
    }, []);

    const updateEnhancedRightClick = useCallback((enabled) => {
        setEnhancedRightClickEnabled(enabled);
    }, []);

    return (
        <ContextMenuContext.Provider
            value={{
                enhancedRightClickEnabled,
                toggleEnhancedRightClick,
                updateEnhancedRightClick,
            }}
        >
            {children}
        </ContextMenuContext.Provider>
    );
};

export default ContextMenuContext;
